// Görevlerim (/tasks): kullanıcıya atanmış cor_tasks kayıtlarının tam listesi.
// Üst menüdeki zil ve ana sayfadaki "Bekleyen İşlerim" kartı buraya yönlenir.

import React, { useState } from 'react';
import { Divider, message, Segmented, Space, Spin, Tag, Typography } from 'antd';
import StandardTable from '../../components/common/StandardTable';
import type { ColumnsType } from 'antd/es/table';
import { CarryOutOutlined, CheckOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useAuth } from '../../components/common/security/AuthContext';
import { useAssignedTasks } from '../../components/common/TaskManagement/useAssignedTasks';
import { taskService } from '../../components/common/TaskManagement/taskService';
import type { CorTask } from '../../types';
import Button from '../../components/common/Button';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

type FilterKey = 'pending' | 'completed' | 'all';

const STATUS_FILTERS: Record<FilterKey, Array<CorTask['status']>> = {
  pending: ['pending', 'in progress', 'blocked'],
  completed: ['completed'],
  all: ['pending', 'in progress', 'blocked', 'completed', 'cancelled'],
};

const STATUS_COLORS: Record<CorTask['status'], string> = {
  pending: 'orange',
  'in progress': 'blue',
  blocked: 'default',
  completed: 'green',
  cancelled: 'red',
};

const CorTasksPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { personnelId } = useAuth();
  const [filter, setFilter] = useState<FilterKey>('pending');
  const { tasks, isLoading, navigateToTask } = useAssignedTasks({ statuses: STATUS_FILTERS[filter] });
  const [completingId, setCompletingId] = useState<number | null>(null);

  const actingPersonnelId = String(localStorage.getItem('personnel_id') || personnelId || '');

  // Yalnızca bilgilendirme (notify) görevleri elle tamamlanabilir;
  // imza/onay (action) görevleri ilgili formda imzalanınca otomatik kapanır.
  const canComplete = (task: CorTask) =>
    task?.context?.purpose === 'notify' && (task.status === 'pending' || task.status === 'in progress');

  const handleComplete = async (task: CorTask) => {
    if (!actingPersonnelId) return;
    setCompletingId(task.id);
    try {
      await taskService.completeTaskById(task.id, actingPersonnelId);
      message.success(t('tasks.completedMsg'));
    } catch (e: any) {
      message.error(e?.message ?? 'Error');
    } finally {
      setCompletingId(null);
    }
  };

  const columns: ColumnsType<CorTask> = [
    {
      title: t('tasks.colType'),
      key: 'type',
      width: 90,
      render: (_, task) =>
        task?.context?.purpose === 'notify'
          ? <Tag color="blue">{t('tasks.typeNotify')}</Tag>
          : <Tag color="orange">{t('tasks.typeAction')}</Tag>,
    },
    {
      title: t('tasks.colDescription'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: t('tasks.colStatus'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: CorTask['status']) => (
        <Tag color={STATUS_COLORS[status] ?? 'default'}>{t(`tasks.status.${status}`)}</Tag>
      ),
    },
    {
      title: t('tasks.colCreated'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (v: string | null) =>
        v ? (
          <Space direction="vertical" size={0}>
            <span>{dayjs(v).format('DD MMM YYYY HH:mm')}</span>
            <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(v).fromNow()}</Text>
          </Space>
        ) : '—',
    },
    {
      title: '',
      key: 'actions',
      width: 180,
      render: (_, task) => (
        <Space>
          {task.navigate_to && (
            <Button size="small" icon={<RightOutlined />} onClick={() => navigateToTask(navigate, task)}>
              {t('tasks.go')}
            </Button>
          )}
          {canComplete(task) && (
            <Button action="edit"
              size="small"
              type="primary"
              ghost
              icon={<CheckOutlined />}
              loading={completingId === task.id}
              onClick={() => handleComplete(task)}
            >
              {t('tasks.complete')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Divider style={{ marginTop: 0, marginBottom: 12 }} />
      <div style={{ padding: '0 16px' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
          <Title level={4} style={{ margin: 0 }}>
            <CarryOutOutlined style={{ marginRight: 8 }} /> {t('tasks.title')}
          </Title>
          <Segmented
            value={filter}
            onChange={v => setFilter(v as FilterKey)}
            options={[
              { label: t('tasks.filterPending'), value: 'pending' },
              { label: t('tasks.filterCompleted'), value: 'completed' },
              { label: t('tasks.filterAll'), value: 'all' },
            ]}
          />
        </Space>
        <Spin spinning={isLoading}>
          <StandardTable
            rowKey="id"
            columns={columns}
            dataSource={tasks}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            locale={{ emptyText: t('tasks.empty') }}
            exportFileName="my_tasks"
          />
        </Spin>
      </div>
    </>
  );
};

export default CorTasksPage;
